/**
 * Health Bar Renderer - High-performance instanced rendering for thousands of units
 * Uses InstancedMesh for minimal draw calls
 */

import * as THREE from 'three';
import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { HealthComponent } from '@/game/components/Health';
import type { FactionComponent } from '@/game/components/Faction';
import type { Camera } from '../Camera';
import { useGameStore } from '@/state/gameStore';
import type { Entity } from '@/types';

// Health bar dimensions
const BAR_WIDTH = 1.2;
const BAR_HEIGHT = 0.1;
const BAR_Y_OFFSET = 2.2;
const MAX_INSTANCES = 10000;
const VIEW_DISTANCE_SQ = 2500; // 50 units squared

export class HealthBarRenderer {
  private scene: THREE.Scene;
  private world: World | null = null;
  private camera: Camera | null = null;

  // Instanced meshes
  private bgMesh: THREE.InstancedMesh;
  private fillMesh: THREE.InstancedMesh;

  // Helper objects
  private dummy = new THREE.Object3D();
  private tempColor = new THREE.Color();
  private localXAxis = new THREE.Vector3();

  // Instance data
  private instanceColors: Float32Array;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Create geometry (plane facing camera)
    const geometry = new THREE.PlaneGeometry(BAR_WIDTH, BAR_HEIGHT);

    // Background material (semi-transparent black)
    const bgMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      depthTest: false,
    });

    // Fill material with vertex colors
    const fillMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
      depthTest: false,
      vertexColors: false,
    });

    // Create instanced meshes
    this.bgMesh = new THREE.InstancedMesh(geometry, bgMaterial, MAX_INSTANCES);
    this.fillMesh = new THREE.InstancedMesh(geometry, fillMaterial.clone(), MAX_INSTANCES);

    // Configure rendering
    this.bgMesh.frustumCulled = false;
    this.fillMesh.frustumCulled = false;
    this.bgMesh.renderOrder = 100;
    this.fillMesh.renderOrder = 101;

    // Initialize instance count to 0
    this.bgMesh.count = 0;
    this.fillMesh.count = 0;

    // Add to scene
    scene.add(this.bgMesh);
    scene.add(this.fillMesh);

    // Color array for fill bars
    this.instanceColors = new Float32Array(MAX_INSTANCES * 3);
  }

  setWorld(world: World): void {
    this.world = world;
  }

  setCamera(camera: Camera): void {
    this.camera = camera;
  }

  update(): void {
    if (!this.world || !this.camera) return;

    const playerFaction = useGameStore.getState().playerFaction;
    const cameraPos = this.camera.camera.position;
    const cameraTarget = this.camera.target;
    const selectedEntities = useGameStore.getState().selectedEntities;
    const selectedSet = new Set(selectedEntities);

    // Query all entities with health
    const entities = this.world.query([
      ComponentTypes.Position,
      ComponentTypes.Health,
      ComponentTypes.Faction,
    ]);

    let index = 0;

    for (const entity of entities) {
      if (index >= MAX_INSTANCES) break;

      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

      // Skip dead entities
      if (health.current <= 0) continue;

      // Calculate health percentage
      const healthPercent = health.current / health.max;
      const isPlayer = faction.factionId === playerFaction;
      const isSelected = selectedSet.has(entity);

      // Skip full health entities (unless selected)
      if (healthPercent >= 1 && !isSelected) continue;

      // Distance culling (from camera target, not camera position)
      const dx = pos.x - cameraTarget.x;
      const dz = pos.z - cameraTarget.z;
      const distSq = dx * dx + dz * dz;
      if (distSq > VIEW_DISTANCE_SQ) continue;

      // Calculate bar position
      const barY = pos.y + BAR_Y_OFFSET;

      // Copy camera rotation so bar is parallel to screen (always appears horizontal)
      this.dummy.quaternion.copy(this.camera.camera.quaternion);

      // Background bar (full width, centered)
      this.dummy.position.set(pos.x, barY, pos.z);
      this.dummy.scale.set(1, 1, 1);
      this.dummy.updateMatrix();
      this.bgMesh.setMatrixAt(index, this.dummy.matrix);

      // Fill bar (scaled by health, offset in local -X direction for left-align)
      const fillOffset = (BAR_WIDTH * (1 - healthPercent)) / 2;
      // Get the local X axis direction from camera orientation
      this.localXAxis.set(1, 0, 0).applyQuaternion(this.dummy.quaternion);
      this.dummy.position.set(
        pos.x - this.localXAxis.x * fillOffset,
        barY - this.localXAxis.y * fillOffset,
        pos.z - this.localXAxis.z * fillOffset
      );
      this.dummy.scale.set(healthPercent, 1, 1);
      this.dummy.updateMatrix();
      this.fillMesh.setMatrixAt(index, this.dummy.matrix);

      // Set color based on health and faction
      const color = this.getHealthColor(healthPercent, isPlayer);
      this.fillMesh.setColorAt(index, color);

      index++;
    }

    // Update instance counts
    this.bgMesh.count = index;
    this.fillMesh.count = index;

    // Mark matrices as needing update
    if (index > 0) {
      this.bgMesh.instanceMatrix.needsUpdate = true;
      this.fillMesh.instanceMatrix.needsUpdate = true;
      if (this.fillMesh.instanceColor) {
        this.fillMesh.instanceColor.needsUpdate = true;
      }
    }
  }

  private getHealthColor(percent: number, isPlayer: boolean): THREE.Color {
    if (isPlayer) {
      // Green -> Yellow -> Red for player
      if (percent > 0.6) {
        this.tempColor.setHex(0x00ff00);
      } else if (percent > 0.3) {
        this.tempColor.setHex(0xffff00);
      } else {
        this.tempColor.setHex(0xff0000);
      }
    } else {
      // Red for enemies
      this.tempColor.setHex(0xff4444);
    }
    return this.tempColor;
  }

  dispose(): void {
    this.scene.remove(this.bgMesh);
    this.scene.remove(this.fillMesh);
    this.bgMesh.geometry.dispose();
    this.fillMesh.geometry.dispose();
    (this.bgMesh.material as THREE.Material).dispose();
    (this.fillMesh.material as THREE.Material).dispose();
  }
}
